import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismButton, PrismIcon } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t, tKey } from 'ember-intl';
import { Illustration } from 'frontend-utils';
import UpsellBannerIllustration from 'lumen/assets/images/benchmark-insight/upsell/beni-upsell-banner-light.svg?raw';

interface HotelBenchmarkInsightUpsellBannerUpsellLightSignature {
  Args: {};
}

export default class HotelBenchmarkInsightUpsellBannerUpsellLight extends Component<HotelBenchmarkInsightUpsellBannerUpsellLightSignature> {
  @service declare access: Services['access'];
  @service('benchmark-insight/trial')
  declare benchmarkInsightTrial: Services['benchmark-insight/trial'];
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];

  benefits = [
    tKey('benchmark_insight.upsell.banner.benefit.1'),
    tKey('benchmark_insight.upsell.banner.benefit.2'),
    tKey('benchmark_insight.upsell.banner.benefit.3'),
  ];

  onClickViewBenchmarkLightTask = task(
    { restartable: true },
    async (): Promise<void> => {
      const { beniUpsellRecord, saveBeniUpsellRecordTask } =
        this.benchmarkInsightTrial;

      if (!beniUpsellRecord) {
        return;
      }

      beniUpsellRecord.lightRequested = true;
      await saveBeniUpsellRecordTask.perform();

      await this.access.refetchFeatureFlags();
      this.router.transitionTo('hotel.benchmark-insight');

      this.usageAnalytics.track('Light Enabled', {
        eventTrigger: 'benchmark-insight-upsell-light',
        product: 'benchmark-insight upsell',
      });
    },
  );

  get isAdminView(): boolean {
    return this.access.hasBenchmarkInsightLightUpsellEdit;
  }

  @action
  onClickLearnMore(): void {
    this.benchmarkInsightTrial.openHelpArticle();

    this.usageAnalytics.track('Light Learned More', {
      eventTrigger: 'benchmark-insight-upsell-light',
      product: 'benchmark-insight upsell',
      isAdminUser: this.access.hasBenchmarkInsightLightUpsellEdit,
    });
  }

  <template>
    <div class="upsell-banner flex flex-row justify-between">

      <div class="upsell-banner__info flex flex-row justify-center gap-1000">
        <h1 class="upsell-banner__info__title">
          {{t "benchmark_insight.upsell.banner.title"}}
        </h1>

        <div class="upsell-banner__info__content">
          <div class="flex flex-col gap-400">
            <div>
              {{t
                "benchmark_insight.upsell.banner.description"
                classNameBold="text-300--bold"
                htmlSafe=true
              }}
            </div>

            {{#each this.benefits as |benefit|}}
              <p class="upsell-banner__info__content__benefit flex flex-row">
                <PrismIcon @name="checkmark" @size="small" />
                <span class="ml-200">
                  {{t benefit}}
                </span>
              </p>
            {{/each}}
          </div>

          <div class="mt-400">
            {{#if this.isAdminView}}
              <PrismButton
                @buttonType="brand"
                @iconAfter="arrow-right"
                @isDisabled={{this.onClickViewBenchmarkLightTask.isRunning}}
                @isLoading={{this.onClickViewBenchmarkLightTask.isRunning}}
                @label={{t
                  "benchmark_insight.upsell.banner.button.get_in_touch"
                }}
                @onClick={{this.onClickViewBenchmarkLightTask.perform}}
                class="w-full"
                data-test-id="upsell-view-benchmark-light-button"
              />

              <PrismButton
                @buttonType="secondary"
                @label={{t "benchmark_insight.upsell.banner.button.learn_more"}}
                @onClick={{this.onClickLearnMore}}
                class="mt-200 w-full"
                data-test-id="upsell-light-learn-more-button"
              />
            {{else}}
              <p>{{t
                  "benchmark_insight.upsell.banner.content.ask_your_admin"
                  classNameBold="text-300--bold"
                  htmlSafe=true
                }}</p>

              <PrismButton
                @buttonType="brand"
                @label={{t "benchmark_insight.upsell.banner.button.learn_more"}}
                @onClick={{this.onClickLearnMore}}
                class="mt-400 w-full"
                data-test-id="upsell-light-learn-more-button"
              />
            {{/if}}
          </div>
        </div>
      </div>

      <div class="upsell-banner__image">
        <Illustration @html={{UpsellBannerIllustration}} />
      </div>
    </div>
  </template>
}
